import { INITIAL_FACT_FIND } from '../../constants/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { AssetType } from '../../../../shared/src/calculation/factFindTypes.ts';

function cloneInitialFactFind(): DraftFactFind {
    return structuredClone(INITIAL_FACT_FIND);
}

function isoDateDaysAgo(daysAgo = 0): string {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return d.toISOString();
}

const FIRST_NAMES = ['Irene', 'John', 'Thomas', 'Gloria', 'Frank', 'Anne', 'Kevin', 'Mei', 'Sarah', 'Raj', 'Helen', 'Robert', 'Nina', 'Oscar', 'Liam', 'Sophia', 'Grace', 'Ethan', 'Mia', 'Lucas', 'Ava', 'Noah', 'Ella', 'James'];
const LAST_NAMES = ['Kowalski', 'Blackwell', 'Reid', 'Morrison', 'Okafor', 'Tan', 'Mitchell', 'Patel', 'Chen', 'Sullivan', 'Hart', 'Wright', 'Brooks', 'Davies', 'Clarke', 'Nguyen'];

const GOAL_SETS = [
    { aged_care: true, meet_costs: true, preserve_assets: true },
    { home_care: true, maximise_support: true },
    { aged_care: true, home_care: true, meet_costs: true, funding_guidance: true },
    { downsize_home: true, preserve_assets: true },
    { retirement_village: true, funding_guidance: true },
];

type HomeDecision = 'keep_home' | 'sell_home' | 'rent_home' | 'downsize_home';
type FundingMode = 'full_rad' | 'partial_rad' | 'dap_only';
type SuggestionSoaSelection = 'optimal_estate' | 'optimal_cashflow' | 'optimal_age_pension' | 'other_from_full_list';

const HOME_DECISIONS: HomeDecision[] = ['keep_home', 'sell_home', 'rent_home', 'downsize_home'];
const FUNDING_MODES: FundingMode[] = ['full_rad', 'partial_rad', 'dap_only'];
const SUGGESTION_SOA_SELECTIONS: SuggestionSoaSelection[] = ['optimal_estate', 'optimal_cashflow', 'optimal_age_pension', 'other_from_full_list'];
const ASSET_SELL_PATTERNS: AssetType[][] = [
    ['bankAccounts'],
    ['bankAccounts', 'shares'],
    ['accountBasedPensions'],
    ['shares', 'superannuation'],
    ['investmentProperty'],
    ['bankAccounts', 'termDeposits', 'shares'],
];

function createDummyFactFind(index: number, isCouple = false): DraftFactFind {
    const fd = cloneInitialFactFind();
    // INITIAL_FACT_FIND always populates these sections, but the type keeps them optional (they're
    // lazily created on some paths) — assert non-null rather than widen every field to `| undefined`.
    const client = fd.client!;
    const partner = fd.partner!;
    const home = fd.home!;
    const expenses = fd.expenses!;
    const family = fd.family!;
    const firstName = FIRST_NAMES[index % FIRST_NAMES.length];
    const lastName = LAST_NAMES[index % LAST_NAMES.length];
    const year = 1940 + (index % 18);
    const month = String((index % 12) + 1).padStart(2, '0');
    const day = String((index % 27) + 1).padStart(2, '0');
    client.firstName = firstName;
    client.lastName = lastName;
    client.dob = `${year}-${month}-${day}`;
    client.currentResidency = index % 3 === 0 ? 'own_home' : 'renting';
    client.healthStatus = index % 4 === 0 ? 'fair' : 'good';
    fd.adviceFor = isCouple ? 'couple' : 'single';
    if (isCouple) {
        partner.firstName = FIRST_NAMES[(index + 3) % FIRST_NAMES.length];
        partner.lastName = lastName;
        partner.dob = `${year + 2}-${month}-${day}`;
        partner.currentResidency = client.currentResidency;
        partner.healthStatus = index % 5 === 0 ? 'fair' : 'good';
    }
    fd.goals = GOAL_SETS[index % GOAL_SETS.length];
    home.value = 700000 + (index % 8) * 50000;
    home.likelyRent = 35000 + (index % 5) * 2500;
    home.likelyExpenses = 9000 + (index % 4) * 1200;
    expenses.livingExpenses = 42000 + (index % 6) * 2800;
    family.childrenCount = index % 4;
    return fd;
}

export type DemoCaseStatus = 'draft' | 'invited' | 'started' | 'submitted' | 'adviser_review' | 'soa_draft' | 'soa_published';
type AccessMode = 'client_end_to_end' | 'client_stop_dashboard' | 'in_person';
type OwnerRole = 'adviser' | 'accountant';
type StrategyUsageMode = 'hybrid' | 'manual_only' | 'suggestion_only';
type StrategySource = 'manual_control' | 'suggestion_engine';
type SoaStatus = 'published' | 'draft' | 'none';

export interface DemoCase {
    id: string;
    clientName: string;
    clientEmail: string;
    ownerId: string;
    ownerName: string;
    ownerRole: OwnerRole;
    practiceId: string;
    accessMode: AccessMode;
    status: DemoCaseStatus;
    profileProgressPct: number;
    startedAt: string;
    lastActivityAt: string;
    submissionDate: string | null;
    soaStatus: SoaStatus;
    agedCareRoomCost: number;
    sahBudget: number;
    commonStrategy: string;
    estateStage: 'unknown' | 'basic' | 'advanced';
    strategySource: StrategySource;
    strategyGenerationCount: number;
    strategyUsageMode: StrategyUsageMode;
    manualStrategiesModelled: number;
    manualStrategiesSaved: number;
    suggestionSoaSelection: SuggestionSoaSelection | null;
    homeDecision: HomeDecision;
    assetSellMix: AssetType[];
    roomFunding: {
        mode: FundingMode;
        radPct: number;
        dapPct: number;
        radAmount: number;
        dapAmount: number;
    };
    factFind: DraftFactFind;
}

/*
 * createProfessionalDemoCases() lived here: twenty-four fabricated client files — names, ages, care
 * goals, pipeline stages — used to seed the portal and to power a "View the demo portal" button.
 *
 * Both are gone (owner's decision, 11 Sep 2026). The button let anyone into the portal UI with no
 * sign-in, unmarked, under a real adviser's name and credential; and seeding the case list meant a
 * real adviser's portal opened holding invented clients until their own data arrived.
 *
 * The TYPES below are still the shape a real client row takes, and getRoleScope is real role
 * scoping — only the invented DATA has gone. If a prospect-facing demo is wanted later it should be
 * built as one: a generic firm name rather than someone's own, sample data marked as sample on
 * screen, and behind a login so it is known who looked.
 */

export type DemoRole = 'licensee' | 'practice_owner' | 'relationship_owner';
export type RoleScope = { type: 'licensee' } | { type: 'practice'; practiceId: string } | { type: 'owner'; ownerId: string };

export function getRoleScope(role: DemoRole): RoleScope {
    if (role === 'licensee') return { type: 'licensee' };
    if (role === 'practice_owner') return { type: 'practice', practiceId: 'prac-harbour' };
    return { type: 'owner', ownerId: 'adv-jm' };
}

